import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useUrlFilters } from "@/lib/urlfilters";
import { peso } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Warning} from "@phosphor-icons/react";

export default function Payroll() {
  const [discard, setDiscard] = useState(null);   // what a recompute would destroy
  const [run, setRun] = useState(null);           // the payroll RUN: net pay, payslips
  const [contrib, setContrib] = useState(null);   // what each agency is owed
  const [replaceRun, setReplaceRun] = useState(null);
  const [confirm, setConfirm] = useState(null);   // what one click is about to cost
  const [busy, setBusy] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [sel, setSel] = useState(null);
  const [lines, setLines] = useState([]);
  const [config, setConfig] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [editLine, setEditLine] = useState(null);
  const [adds, setAdds] = useState([]);
  const [deds, setDeds] = useState([]);

  const [uncovered, setUncovered] = useState(null);
  // ?period=<id> from the dashboard's LATEST PAYROLL PERIOD tile.
  const [f] = useUrlFilters({ period: "" });

  const load = () => {
    api.get("/payroll/periods").then((r) => setPeriods(r.data)).catch((e) => toast.error(errMsg(e)));
    // Approved work that sits outside every cut-off. Its own request, so a
    // failure here never costs the clerk the period list.
    api.get("/payroll/uncovered").then((r) => setUncovered(r.data)).catch(() => setUncovered(null));
  };
  useEffect(() => { load(); api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  // A link naming a period opens it, once, when the list arrives. Without the
  // guard it would re-open on every refresh and fight the clerk's own clicks.
  const [jumped, setJumped] = useState(false);
  useEffect(() => {
    if (jumped || !f.period || !periods.length) return;
    const p = periods.find((x) => x.id === f.period);
    if (p) { setJumped(true); pick(p); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `pick` is
    // recreated every render; listing it would re-run this on each one, and the
    // `jumped` guard exists precisely so it opens the period once.
  }, [f.period, periods, jumped]);

  // Pre-fill a cut-off over ONE contiguous gap, named the way the existing ones
  // are. The clerk still reads it and presses the button; nothing creates a
  // payroll period on its own.
  //
  // ⚠️ PER GAP, never first-uncovered-day to last. The uncovered days here were
  // early August AND mid-September with two CLOSED cut-offs between them, so a
  // single range across the lot would have swallowed both and paid their guards
  // a second time. The server refuses an overlap now; the screen must not
  // propose one either.
  const coverGap = (g) => {
    if (!g) return;
    const a = new Date(`${g.from_date}T00:00:00+08:00`);
    const b = new Date(`${g.to_date}T00:00:00+08:00`);
    const mon = (x) => x.toLocaleDateString("en-PH", { timeZone: "Asia/Manila", month: "short" });
    const name = mon(a) === mon(b)
      ? `${mon(a)} ${a.getDate()}-${b.getDate()}, ${b.getFullYear()} cut-off`
      : `${mon(a)} ${a.getDate()} - ${mon(b)} ${b.getDate()}, ${b.getFullYear()} cut-off`;
    setForm({ name, start_date: g.from_date, end_date: g.to_date });
    setNewOpen(true);
  };

  // A run is a SNAPSHOT, so it is fetched rather than derived from the lines on
  // screen: reprinting a payslip must show what was paid, not what the hours
  // would compute to now.
  const loadRun = async (periodId) => {
    try {
      const { data } = await api.get(`/payroll/periods/${periodId}/run`);
      setRun(data);
      const c = await api.get(`/payroll/periods/${periodId}/contributions`);
      setContrib(c.data);
    } catch { setRun(null); setContrib(null); }
  };

  const doRun = async (force = false) => {
    try {
      const { data } = await api.post(`/payroll/periods/${sel.id}/run`, { force });
      setRun(data);
      const c = await api.get(`/payroll/periods/${sel.id}/contributions`);
      setContrib(c.data);
      toast.success(`Payroll run ${data.ref_no} — ${data.totals.headcount} payslips, net ${peso(data.totals.net)}`);
      if (data.unreviewed_tables?.length) {
        toast.warning(`Unverified rates: ${data.unreviewed_tables.join(", ").toUpperCase()}`);
      }
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.run) { setReplaceRun(d.run); return; }
      toast.error(errMsg(e));
    }
  };

  // ONE ACTION CLOSES A CUT-OFF: compute, lock, run.
  //
  // It used to be four clicks in an order you had to already know -- compute,
  // lock, approve, run -- with nothing on screen saying what came next. The
  // lifecycle is the app's business, not the payroll clerk's.
  //
  // It shortens the path, it does not widen it: every refusal the long way
  // round would raise is raised here, and this catches BOTH of them and shows
  // what the shortcut would cost before anything happens.
  const finalize = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/periods/${sel.id}/finalize`, { force });
      setRun(data.run);
      const c = await api.get(`/payroll/periods/${sel.id}/contributions`);
      setContrib(c.data);
      toast.success(`${data.run.ref_no} — ${data.run.totals.headcount} payslips, net ${peso(data.run.totals.net)}`);
      await load();
      const fresh = (await api.get("/payroll/periods")).data.find((x) => x.id === sel.id);
      if (fresh) setSel(fresh);
      const ls = await api.get(`/payroll/periods/${sel.id}/lines`);
      setLines(ls.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && (d?.discards || d?.run)) { setConfirm(d); return; }
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const pick = async (p) => {
    const { data } = await api.get(`/payroll/periods/${p.id}/lines`);
    setLines(data);
    setSel(p);
    loadRun(p.id);
  };

  const refresh = async () => {
    const { data } = await api.get("/payroll/periods");
    setPeriods(data);
    if (sel) {
      const fresh = data.find((x) => x.id === sel.id);
      if (fresh) setSel(fresh);
      const l = await api.get(`/payroll/periods/${sel.id}/lines`);
      setLines(l.data);
    }
  };

  const createPeriod = async () => {
    try {
      await api.post("/payroll/periods", form);
      toast.success("Payroll period opened");
      setNewOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // A recompute REBUILDS every line, so the COLA / SSS / PhilHealth / Pag-IBIG
  // figures a clerk typed are discarded. The server refuses with 409 and names
  // exactly what is at stake; this renders those numbers rather than asking "are
  // you sure", because a confirmation that does not say what it is about to
  // destroy is one people learn to click through.
  const compute = async (force = false) => {
    try {
      const { data } = await api.post(`/payroll/periods/${sel.id}/compute`, { force });
      toast.success(`Computed ${data.lines} lines from ${data.shifts_processed} approved shifts`);
      if (data.discarded) {
        toast.warning(`Discarded hand-entered adjustments on ${data.discarded.lines} line(s)`);
      }
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.discards) {
        setDiscard(d.discards);
        return;
      }
      toast.error(errMsg(e));
    }
  };

  const transition = async (to) => {
    try {
      await api.post(`/payroll/periods/${sel.id}/transition`, { to });
      toast.success(`Period → ${to}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportFile = (format) => {
    window.open(`${API}/payroll/periods/${sel.id}/export?format=${format}`, "_blank");
    setTimeout(refresh, 1500);
  };

  const openEdit = (line) => {
    setEditLine(line);
    setAdds(line.additions || []);
    setDeds(line.deductions || []);
  };

  const saveLine = async () => {
    try {
      await api.patch(`/payroll/lines/${editLine.id}`, { additions: adds.filter((a) => a.label), deductions: deds.filter((d) => d.label) });
      toast.success("Line updated");
      setEditLine(null);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const FLOW_ACTIONS = { open: [["locked", "Lock period"]], locked: [["approved", "Approve"]], approved: [], exported: [["closed", "Close period"]] };

  return (
    <div data-testid="payroll-page">
      <PageHeader title="Payroll Preparation" sub="Hours from approved attendance, then a run: SSS, PhilHealth, Pag-IBIG, withholding tax, net pay, payslips and remittance schedules."
        actions={<Button data-testid="new-period-button" className="rounded-none" onClick={() => setNewOpen(true)}>New period</Button>} />

      {/*
        ⚠️ THE PAYROLL TWIN OF THE UNBILLED-SHIFT AUDIT. Billing has a check for
        work nobody ever charged for; payroll had none for work nobody ever PAID
        for. Twenty-two approved shifts sat outside every cut-off here, visible
        only as a footnote on a leaver's final-pay screen — the last possible
        moment to find it.
      */}
      {uncovered?.shifts > 0 && (
        <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 mb-4 flex flex-wrap items-start gap-3"
             data-testid="payroll-uncovered">
          <Warning size={18} weight="fill" className="text-[hsl(var(--alert))] shrink-0 mt-0.5" />
          <div className="text-xs leading-snug flex-1 min-w-[260px]">
            <span className="font-bold">
              {uncovered.shifts} approved shift(s) for {uncovered.guards} guard(s) are not in any cut-off.
            </span>{" "}
            Nobody has been paid for them and nothing picks them up on its own. The hours are already
            approved, so computing is all that is left.
            <div className="mt-2 space-y-1">
              {(uncovered.gaps || []).map((g) => (
                <div key={g.from_date} className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[11px]">
                    {g.from_date} → {g.to_date} · {g.shifts} shift(s), {g.guards} guard(s)
                  </span>
                  <Button size="sm" className="rounded-none h-7 text-[11px]"
                          data-testid={`payroll-cover-${g.from_date}`}
                          onClick={() => coverGap(g)}>
                    Open a cut-off for these
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <DataTable testid="periods-table" onRowClick={pick} rows={periods} empty="No payroll periods yet"
        columns={[
          { key: "ref_no", label: "Ref", render: (r) => <span className="font-mono text-xs">{r.ref_no}</span> },
          { key: "name", label: "Period", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.start_date} → {r.end_date}</div></div> },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "line_count", label: "Lines", render: (r) => <span className="font-mono text-xs">{r.line_count ?? "—"}</span> },
        ]} />

      {sel && (
        <div className="mt-8" data-testid="period-detail">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="font-heading text-lg font-bold">{sel.name} <StatusBadge value={sel.status} /></h2>
            <div className="flex flex-wrap gap-2">
              {/* THE primary action, at the top. It was in the run panel below
                  the fold, and an action below the fold reads as missing --
                  people scrolled past a screen that could close their fortnight
                  in one click. Everything else here is secondary by design. */}
              <Button size="sm" className="rounded-none" data-testid="finalize-button" disabled={busy}
                onClick={() => finalize()}>
                {busy ? "Working…" : run ? "Re-run this cut-off" : "Complete this cut-off"}
              </Button>
              {sel.status === "open" && (
                <Button size="sm" variant="outline" className="rounded-none" data-testid="compute-button"
                  onClick={() => compute()}>Compute hours only</Button>
              )}
              {(FLOW_ACTIONS[sel.status] || []).map(([to, label]) => (
                <Button key={to} size="sm" variant="outline" className="rounded-none" data-testid={`period-to-${to}`} onClick={() => transition(to)}>{label}</Button>
              ))}
              {["approved", "exported"].includes(sel.status) && (
                <>
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="export-csv" onClick={() => exportFile("csv")}>Export CSV</Button>
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="export-xlsx" onClick={() => exportFile("xlsx")}>Export XLSX</Button>
                </>
              )}
            </div>
          </div>
          {/* Where the cut-off is, in one line, so nobody has to infer it from
              which buttons happen to be enabled. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] uppercase tracking-[0.15em] mb-4"
            data-testid="cutoff-progress">
            {[["Hours computed", lines.length > 0, `${lines.length} line(s)`],
              ["Hours locked", ["locked", "approved", "exported", "closed"].includes(sel.status), sel.status],
              ["Payroll run", !!run, run ? `${run.totals.headcount} payslip(s)` : "not yet"],
            ].map(([label, done, detail], i) => (
              <span key={label} className="flex items-center gap-2">
                {i > 0 && <span className="text-muted-foreground">→</span>}
                <span className={done ? "text-[hsl(var(--success))]" : "text-muted-foreground"}>
                  {done ? "✓" : "○"} {label}
                  <span className="text-muted-foreground normal-case tracking-normal"> ({detail})</span>
                </span>
              </span>
            ))}
          </div>

          <DataTable testid="payroll-lines-table" rows={lines} empty="No lines — run compute first"
            columns={[
              { key: "guard", label: "Guard", render: (r) => <div><div className="font-semibold">{r.guard_name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.employee_no}</div></div> },
              { key: "reg", label: "Reg h", render: (r) => <span className="font-mono text-xs">{r.hours.regular}</span> },
              { key: "ot", label: "OT h", render: (r) => <span className="font-mono text-xs">{r.hours.ot}</span> },
              { key: "rest", label: "Rest h", render: (r) => <span className="font-mono text-xs">{r.hours.rest_day}</span> },
              { key: "hol", label: "Holiday h", render: (r) => <span className="font-mono text-xs">{r.hours.holiday}</span> },
              { key: "nd", label: "ND h", render: (r) => <span className="font-mono text-xs">{r.hours.night_diff}</span> },
              { key: "leave", label: "Leave h", render: (r) => <span className="font-mono text-xs">{r.hours.leave}</span> },
              { key: "rate", label: "Rates (d/h)", render: (r) => <span className="font-mono text-xs">{peso(r.daily_rate)} / {peso(r.hourly_rate)}</span> },
              { key: "adj", label: "Add / Ded", render: (r) => <span className="font-mono text-xs">{peso((r.additions || []).reduce((s, a) => s + (a.amount || 0), 0))} / {peso((r.deductions || []).reduce((s, d) => s + (d.amount || 0), 0))}</span> },
              { key: "actions", label: "", render: (r) => sel.status === "open" && <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" data-testid={`edit-line-${r.employee_no}`} onClick={() => openEdit(r)}>Edit +/−</Button> },
            ]} />
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none" data-testid="new-period-dialog">
          <DialogHeader><DialogTitle className="font-heading">New payroll period</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Name</Label><Input data-testid="period-name" className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="June 2026 — 1st half" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Start</Label><Input data-testid="period-start" type="date" className="rounded-none mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">End</Label><Input data-testid="period-end" type="date" className="rounded-none mt-1" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <Button data-testid="period-create-submit" className="w-full rounded-none" onClick={createPeriod} disabled={!form.name || !form.start_date || !form.end_date}>Open period</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editLine} onOpenChange={() => setEditLine(null)}>
        <DialogContent className="rounded-none max-h-[85vh] overflow-auto" data-testid="edit-line-dialog">
          <DialogHeader><DialogTitle className="font-heading">Additions / deductions — {editLine?.guard_name}</DialogTitle></DialogHeader>
          {[["Additions", adds, setAdds, config?.addition_types || [], "addition"], ["Deductions", deds, setDeds, config?.deduction_types || [], "deduction"]].map(([title, items, setItems, types, key]) => (
            <div key={key} className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-wider font-bold">{title}</div>
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" data-testid={`add-${key}-row`} onClick={() => setItems([...items, { label: "", amount: 0 }])}><Plus size={12} /></Button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_100px_32px] gap-2 mb-1">
                  <Select value={it.label} onValueChange={(v) => { const n = [...items]; n[i] = { ...n[i], label: v }; setItems(n); }}>
                    <SelectTrigger data-testid={`${key}-label-${i}`} className="rounded-none h-8"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid={`${key}-amount-${i}`} type="number" className="rounded-none h-8" value={it.amount} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], amount: parseFloat(e.target.value) || 0 }; setItems(n); }} />
                  <Button size="sm" variant="ghost" className="h-8 px-2" data-testid={`${key}-del-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash size={14} /></Button>
                </div>
              ))}
            </div>
          ))}
          <Button data-testid="line-save" className="w-full rounded-none" onClick={saveLine}>Save line</Button>
        </DialogContent>
      </Dialog>
      {sel && (
        <div className="mt-8" data-testid="payroll-run-panel">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="font-heading text-lg font-bold leading-none">Payroll run</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Net pay, payslips and government remittance schedules. A run is a snapshot —
                payslips are reprinted from it, never recomputed.
              </p>
            </div>
            {/* No button here: the primary action lives at the top of the
                period, and two buttons that do the same thing make a reader
                stop and work out whether they differ. */}
          </div>

          {run?.duplicate_statutory?.length > 0 && (
            <div className="border border-[hsl(var(--alert))] p-3 mb-3" data-testid="double-deduction">
              <div className="font-heading text-xs font-bold text-[hsl(var(--alert))] uppercase tracking-wider">
                Possible double deduction — {run.duplicate_statutory.length} line(s)
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                These lines carry hand-entered deductions named like the contributions this run computes,
                so the guard is deducted <span className="font-bold">twice</span>:{" "}
                {run.duplicate_statutory.slice(0, 4).map((d) => `${d.guard_name} (${d.labels.join(", ")})`).join(" · ")}
                {run.duplicate_statutory.length > 4 ? ` · +${run.duplicate_statutory.length - 4} more` : ""}.
                Reopen the period, remove those rows, and re-run. The payslips carry the same warning.
              </p>
            </div>
          )}

          {run?.unreviewed_tables?.length > 0 && (
            <div className="border border-[hsl(var(--alert))] p-3 mb-3" data-testid="unverified-rates">
              <div className="font-heading text-xs font-bold text-[hsl(var(--alert))] uppercase tracking-wider">
                Unverified contribution rates: {run.unreviewed_tables.join(" · ").toUpperCase()}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                These schedules were seeded from published figures and <span className="font-bold">have not been
                confirmed against the current circular</span>. The payslips and remittance files below carry the
                same warning. An administrator can check and sign them off in Settings → Statutory rates.
              </p>
            </div>
          )}

          {!run && (
            <p className="font-mono text-[10px] text-muted-foreground">
              No run yet. <span className="font-bold">Complete this cut-off</span> computes the hours from
              approved attendance, locks them, and produces the payslips — in one step.
            </p>
          )}

          {run && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border mb-4">
                {[["Gross", run.totals.gross], ["Contributions (EE)", run.totals.statutory_employee],
                  ["Withholding tax", run.totals.withholding], ["Other deductions", run.totals.other_deductions],
                  ["Net pay", run.totals.net]].map(([label, v], i) => (
                  <div key={label} className={`bg-background p-3 ${i === 4 ? "md:bg-foreground md:text-background" : ""}`}>
                    <div className="font-mono text-[9px] uppercase tracking-[0.15em] opacity-70">{label}</div>
                    <div className="font-heading text-base font-bold mt-1">{peso(v)}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                <Button size="sm" className="rounded-none" data-testid="payslips-zip"
                  onClick={() => window.open(`${API}/payroll/periods/${sel.id}/payslips.zip`, "_blank")}>
                  Download all {run.totals.headcount} payslips
                </Button>
                <Button size="sm" variant="outline" className="rounded-none" data-testid="contributions-workbook"
                  onClick={() => window.open(`${API}/payroll/periods/${sel.id}/contributions.xlsx`, "_blank")}>
                  Download all contribution schedules
                </Button>
              </div>

              <DataTable testid="payslips-table" rows={run.slips} empty="No payslips"
                columns={[
                  { key: "guard", label: "Guard", render: (r) => (
                    <div><div className="font-semibold">{r.guard_name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{r.employee_no}</div></div>) },
                  { key: "gross", label: "Gross", render: (r) => <span className="font-mono text-xs">{peso(r.gross)}</span> },
                  { key: "sss", label: "SSS", render: (r) => <span className="font-mono text-xs">{peso(r.statutory.sss.employee_this_period)}</span> },
                  { key: "phic", label: "PhilHealth", render: (r) => <span className="font-mono text-xs">{peso(r.statutory.philhealth.employee_this_period)}</span> },
                  { key: "hdmf", label: "Pag-IBIG", render: (r) => <span className="font-mono text-xs">{peso(r.statutory.pagibig.employee_this_period)}</span> },
                  { key: "tax", label: "W/tax", render: (r) => <span className="font-mono text-xs">{peso(r.withholding.this_period)}</span> },
                  { key: "net", label: "Net pay", render: (r) => <span className="font-mono text-xs font-bold">{peso(r.net)}</span> },
                  { key: "slip", label: "Payslip", render: (r) => (
                    <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider"
                      data-testid={`payslip-${r.guard_id}`}
                      onClick={() => window.open(`${API}/payroll/periods/${sel.id}/payslips/${r.id}.pdf`, "_blank")}>PDF</Button>) },
                ]} />

              {contrib && (
                <div className="mt-6">
                  <h3 className="font-heading text-sm font-bold">Government contributions</h3>
                  <p className="text-xs text-muted-foreground mt-1 mb-3">
                    Remittance schedules for this cut-off. Employer shares are paid by the agency and are not
                    deducted from anyone.
                  </p>
                  <div className="border border-border">
                    <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 px-3 py-2 border-b border-border font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      <span>Agency</span><span>Employee</span><span>Employer</span><span>Total</span><span>Schedule</span>
                    </div>
                    {Object.entries(contrib.agencies).map(([key, a]) => (
                      <div key={key} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 px-3 py-2 items-center border-b border-border last:border-b-0">
                        <div className="font-semibold text-xs">{a.label}
                          <span className="font-mono text-[10px] text-muted-foreground ml-2">{a.headcount} employee{a.headcount === 1 ? "" : "s"}</span>
                        </div>
                        <span className="font-mono text-xs text-right w-24">{peso(a.employee)}</span>
                        <span className="font-mono text-xs text-right w-24">{peso(a.employer)}</span>
                        <span className="font-mono text-xs text-right w-24 font-bold">{peso(a.total)}</span>
                        <div className="flex gap-1">
                          {["csv", "xlsx"].map((f) => (
                            <Button key={f} size="sm" variant="outline" data-testid={`contrib-${key}-${f}`}
                              className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider"
                              onClick={() => window.open(`${API}/payroll/periods/${sel.id}/contributions/${key}?format=${f}`, "_blank")}>{f}</Button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="font-mono text-[10px] text-muted-foreground mt-2">
                    {run.ref_no} · computed {String(run.created_at).slice(0, 19).replace("T", " ")} by {run.created_by}
                    {" · "}factor {run.factor} · {run.periods_per_month} cut-off{run.periods_per_month === 1 ? "" : "s"} per month
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ONE dialog for the one action. It shows whichever of the two costs
          actually apply -- discarded adjustments, a replaced run, or both --
          because the point of collapsing four clicks into one is to remove the
          steps, not the information. */}
      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="rounded-none" data-testid="finalize-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading">Before this cut-off is closed</DialogTitle></DialogHeader>
          {confirm?.discards && (
            <div className="border border-border p-3 mb-2">
              <div className="font-heading text-xs font-bold uppercase tracking-wider">Hand-entered adjustments will be discarded</div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                Recomputing rebuilds every line from the approved attendance.
                <span className="font-bold text-foreground"> {confirm.discards.lines} of {confirm.discards.of_lines} line(s)</span>{" "}
                carry {peso(confirm.discards.additions_total)} in additions and{" "}
                {peso(confirm.discards.deductions_total)} in deductions typed by hand
                {confirm.discards.labels?.length ? ` (${confirm.discards.labels.join(", ")})` : ""}. They are not recoverable.
              </p>
            </div>
          )}
          {confirm?.run && (
            <div className="border border-border p-3 mb-2">
              <div className="font-heading text-xs font-bold uppercase tracking-wider">An existing payroll run will be replaced</div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                <span className="font-bold text-foreground">{confirm.run.ref_no}</span> was computed{" "}
                {String(confirm.run.created_at || "").slice(0, 19).replace("T", " ")} by {confirm.run.created_by},
                net {peso(confirm.run.net_total)}. Every payslip already handed out was printed from it and
                would stop matching.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" className="rounded-none" data-testid="finalize-cancel"
              onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="rounded-none" data-testid="finalize-confirm"
              onClick={() => { setConfirm(null); finalize(true); }}>Continue anyway</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Re-running REPLACES the snapshot every payslip and remittance report is
          printed from, so it names the run it would overwrite rather than asking
          a generic "are you sure". */}
      <Dialog open={!!replaceRun} onOpenChange={() => setReplaceRun(null)}>
        <DialogContent className="rounded-none" data-testid="replace-run-dialog">
          <DialogHeader><DialogTitle className="font-heading">Replace the existing payroll run?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-bold text-foreground">{replaceRun?.ref_no}</span> was computed{" "}
            {String(replaceRun?.created_at || "").slice(0, 19).replace("T", " ")} by {replaceRun?.created_by},
            with a net total of <span className="font-bold text-foreground">{peso(replaceRun?.net_total)}</span>.
            Every payslip and remittance schedule is printed from that snapshot. Replacing it means any payslip
            already handed out no longer matches what the system holds.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button variant="outline" className="rounded-none" data-testid="replace-run-cancel"
              onClick={() => setReplaceRun(null)}>Keep it</Button>
            <Button className="rounded-none" data-testid="replace-run-confirm"
              onClick={() => { setReplaceRun(null); doRun(true); }}>Replace and re-run</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!discard} onOpenChange={() => setDiscard(null)}>
        <DialogContent className="rounded-none" data-testid="recompute-warning-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Recomputing will discard hand-entered adjustments</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Recomputing rebuilds every line from the approved attendance. The additions and
            deductions below were typed by hand and are <span className="font-bold text-foreground">not
            recoverable</span> — nor are any remarks on those lines.
          </p>
          <div className="border border-border p-3 mt-2 space-y-2">
            <div className="flex justify-between font-mono text-xs">
              <span className="text-muted-foreground uppercase tracking-wider">Lines affected</span>
              <span className="font-bold" data-testid="discard-lines">{discard?.lines} of {discard?.of_lines}</span>
            </div>
            <div className="flex justify-between font-mono text-xs">
              <span className="text-muted-foreground uppercase tracking-wider">Additions</span>
              <span className="font-bold">{peso(discard?.additions_total)}</span>
            </div>
            <div className="flex justify-between font-mono text-xs">
              <span className="text-muted-foreground uppercase tracking-wider">Deductions</span>
              <span className="font-bold">{peso(discard?.deductions_total)}</span>
            </div>
            {discard?.labels?.length > 0 && (
              <div className="font-mono text-[10px] text-muted-foreground pt-1 border-t border-border">
                {discard.labels.join(" · ")}
              </div>
            )}
            {discard?.guards?.length > 0 && (
              <div className="font-mono text-[10px] text-muted-foreground">
                {discard.guards.join(", ")}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button variant="outline" className="rounded-none" data-testid="recompute-cancel"
              onClick={() => setDiscard(null)}>Keep the adjustments</Button>
            <Button className="rounded-none" data-testid="recompute-confirm"
              onClick={() => { setDiscard(null); compute(true); }}>Discard and recompute</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
